import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft, Plus, Check, X, Trash2, Archive, RotateCcw, Users, BookOpen, Pencil, Mic } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

// Service types with prices for horses
const SERVICE_OPTIONS = [
  { type: 'training', label: 'Træning', prices: [1400, 1100, 800] },
  { type: 'for_sale', label: 'Til Salg', prices: [800, 600] },
  { type: 'saddled', label: 'Opsadlet', prices: [600] }
];

const AdminJCPPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [horses, setHorses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddHorseForm, setShowAddHorseForm] = useState(false);
  const [showAddCourseForm, setShowAddCourseForm] = useState(false);
  const [showEditCustomerForm, setShowEditCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'horses', 'courses', 'lessons', or 'provisions'
  const [expandedCustomers, setExpandedCustomers] = useState({}); // Track which customers are expanded
  
  // Horse form
  const [horseForm, setHorseForm] = useState({
    customer_id: '',
    new_customer_first_name: '',
    new_customer_last_name: '',
    is_new_customer: true,
    horse_name: '',
    start_date: new Date().toISOString().split('T')[0],
    service_type: 'training',
    price_per_month: 1400,
    use_custom_price: false,
    custom_price: ''
  });

  // Course form
  const [courseForm, setCourseForm] = useState({
    customer_id: '',
    new_customer_first_name: '',
    new_customer_last_name: '',
    is_new_customer: true,
    course_date: new Date().toISOString().split('T')[0],
    num_days: 1,
    description: ''
  });

  // Lesson form
  const [showAddLessonForm, setShowAddLessonForm] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [lessonForm, setLessonForm] = useState({
    customer_id: '',
    new_customer_first_name: '',
    new_customer_last_name: '',
    is_new_customer: true,
    lesson_date: new Date().toISOString().split('T')[0],
    num_sessions: 1,
    price_per_session: 65,
    custom_price: '',
    use_custom_price: false,
    description: ''
  });

  // Provision form (horse sale commission)
  const [showAddProvisionForm, setShowAddProvisionForm] = useState(false);
  const [provisions, setProvisions] = useState([]);
  const [provisionForm, setProvisionForm] = useState({
    customer_id: '',
    new_customer_first_name: '',
    new_customer_last_name: '',
    is_new_customer: true,
    provision_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: ''
  });

  // Edit states
  const [editingHorse, setEditingHorse] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingProvision, setEditingProvision] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) {
      fetchAllData();
    }
  }, [user]);

  const fetchAllData = async () => {
    try {
      const [customersRes, horsesRes, coursesRes, lessonsRes, provisionsRes] = await Promise.all([
        axios.get(`${API}/admin/jcp-customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jcp-horses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jcp-courses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jcp-lessons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jcp-provisions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCustomers(customersRes.data);
      setHorses(horsesRes.data);
      setCourses(coursesRes.data);
      setLessons(lessonsRes.data);
      setProvisions(provisionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCustomerIfNeeded = async (isNew, customerId, firstName, lastName) => {
    if (!isNew && customerId) {
      return customerId;
    }
    // Create new customer
    const res = await axios.post(`${API}/admin/jcp-customers`, {
      first_name: firstName,
      last_name: lastName
    }, { headers: { Authorization: `Bearer ${token}` } });
    return res.data.id;
  };

  const handleAddHorse = async (e) => {
    e.preventDefault();
    try {
      const customerId = await createCustomerIfNeeded(
        horseForm.is_new_customer,
        horseForm.customer_id,
        horseForm.new_customer_first_name,
        horseForm.new_customer_last_name
      );

      // Use custom price if selected, otherwise use selected price
      const finalPrice = horseForm.use_custom_price 
        ? parseInt(horseForm.custom_price) 
        : horseForm.price_per_month;

      await axios.post(`${API}/admin/jcp-horses`, {
        customer_id: customerId,
        horse_name: horseForm.horse_name,
        start_date: horseForm.start_date,
        service_type: horseForm.service_type,
        price_per_month: finalPrice
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Hest tilføjet');
      setShowAddHorseForm(false);
      resetHorseForm();
      fetchAllData();
    } catch (error) {
      console.error('Error adding horse:', error);
      toast.error('Kunne ikke tilføje');
    }
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    try {
      const customerId = await createCustomerIfNeeded(
        courseForm.is_new_customer,
        courseForm.customer_id,
        courseForm.new_customer_first_name,
        courseForm.new_customer_last_name
      );

      await axios.post(`${API}/admin/jcp-courses`, {
        customer_id: customerId,
        course_date: courseForm.course_date,
        num_days: courseForm.num_days,
        description: courseForm.description
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Kursus tilføjet');
      setShowAddCourseForm(false);
      resetCourseForm();
      fetchAllData();
    } catch (error) {
      console.error('Error adding course:', error);
      toast.error('Kunne ikke tilføje');
    }
  };

  const resetHorseForm = () => {
    setHorseForm({
      customer_id: '',
      new_customer_first_name: '',
      new_customer_last_name: '',
      is_new_customer: true,
      horse_name: '',
      start_date: new Date().toISOString().split('T')[0],
      service_type: 'training',
      price_per_month: 1400,
      use_custom_price: false,
      custom_price: ''
    });
  };

  const resetCourseForm = () => {
    setCourseForm({
      customer_id: '',
      new_customer_first_name: '',
      new_customer_last_name: '',
      is_new_customer: true,
      course_date: new Date().toISOString().split('T')[0],
      num_days: 1,
      description: ''
    });
  };

  const resetLessonForm = () => {
    setLessonForm({
      customer_id: '',
      new_customer_first_name: '',
      new_customer_last_name: '',
      is_new_customer: true,
      lesson_date: new Date().toISOString().split('T')[0],
      num_sessions: 1,
      price_per_session: 65,
      custom_price: '',
      use_custom_price: false,
      description: ''
    });
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    try {
      const customerId = await createCustomerIfNeeded(
        lessonForm.is_new_customer,
        lessonForm.customer_id,
        lessonForm.new_customer_first_name,
        lessonForm.new_customer_last_name
      );

      const pricePerSession = lessonForm.use_custom_price 
        ? parseFloat(lessonForm.custom_price) 
        : lessonForm.price_per_session;

      await axios.post(`${API}/admin/jcp-lessons`, {
        customer_id: customerId,
        lesson_date: lessonForm.lesson_date,
        num_sessions: lessonForm.num_sessions,
        price_per_session: pricePerSession,
        description: lessonForm.description
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Undervisning tilføjet');
      setShowAddLessonForm(false);
      resetLessonForm();
      fetchAllData();
    } catch (error) {
      console.error('Error adding lesson:', error);
      toast.error('Kunne ikke tilføje');
    }
  };

  const toggleLessonPayment = async (lessonId) => {
    try {
      await axios.put(`${API}/admin/jcp-lessons/${lessonId}/toggle-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleLessonActive = async (lessonId) => {
    try {
      await axios.put(`${API}/admin/jcp-lessons/${lessonId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Slet denne undervisning?')) return;
    try {
      await axios.delete(`${API}/admin/jcp-lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slettet');
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke slette');
    }
  };

  // Provision handlers
  const resetProvisionForm = () => {
    setProvisionForm({
      customer_id: '',
      new_customer_first_name: '',
      new_customer_last_name: '',
      is_new_customer: true,
      provision_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: ''
    });
  };

  const handleAddProvision = async (e) => {
    e.preventDefault();
    try {
      const customerId = await createCustomerIfNeeded(
        provisionForm.is_new_customer,
        provisionForm.customer_id,
        provisionForm.new_customer_first_name,
        provisionForm.new_customer_last_name
      );

      await axios.post(`${API}/admin/jcp-provisions`, {
        customer_id: customerId,
        provision_date: provisionForm.provision_date,
        amount: parseFloat(provisionForm.amount),
        description: provisionForm.description
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Provision tilføjet');
      setShowAddProvisionForm(false);
      resetProvisionForm();
      fetchAllData();
    } catch (error) {
      console.error('Error adding provision:', error);
      toast.error('Kunne ikke tilføje');
    }
  };

  const toggleProvisionPayment = async (provisionId) => {
    try {
      await axios.put(`${API}/admin/jcp-provisions/${provisionId}/toggle-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleProvisionActive = async (provisionId) => {
    try {
      await axios.put(`${API}/admin/jcp-provisions/${provisionId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const deleteProvision = async (provisionId) => {
    if (!window.confirm('Slet denne provision?')) return;
    try {
      await axios.delete(`${API}/admin/jcp-provisions/${provisionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slettet');
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke slette');
    }
  };

  // Edit handlers
  const handleEditHorse = async (e) => {
    e.preventDefault();
    if (!editingHorse) return;
    try {
      await axios.put(`${API}/admin/jcp-horses/${editingHorse.id}`, {
        horse_name: editingHorse.horse_name,
        service_type: editingHorse.service_type,
        price_per_month: editingHorse.price_per_month
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Opdateret');
      setEditingHorse(null);
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const handleEditCourse = async (e) => {
    e.preventDefault();
    if (!editingCourse) return;
    try {
      await axios.put(`${API}/admin/jcp-courses/${editingCourse.id}`, {
        course_date: editingCourse.course_date,
        num_days: editingCourse.num_days,
        total_price: editingCourse.total_price,
        description: editingCourse.description
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Opdateret');
      setEditingCourse(null);
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const handleEditLesson = async (e) => {
    e.preventDefault();
    if (!editingLesson) return;
    try {
      await axios.put(`${API}/admin/jcp-lessons/${editingLesson.id}`, {
        lesson_date: editingLesson.lesson_date,
        num_sessions: editingLesson.num_sessions,
        price_per_session: editingLesson.price_per_session,
        description: editingLesson.description
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Opdateret');
      setEditingLesson(null);
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const handleEditProvision = async (e) => {
    e.preventDefault();
    if (!editingProvision) return;
    try {
      await axios.put(`${API}/admin/jcp-provisions/${editingProvision.id}`, {
        provision_date: editingProvision.provision_date,
        amount: editingProvision.amount,
        description: editingProvision.description
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Opdateret');
      setEditingProvision(null);
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleHorsePayment = async (horseId, month, currentlyPaid) => {
    try {
      await axios.put(`${API}/admin/jcp-horses/${horseId}/payment`, {
        month, is_paid: !currentlyPaid
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleCoursePayment = async (courseId) => {
    try {
      await axios.put(`${API}/admin/jcp-courses/${courseId}/toggle-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleHorseActive = async (horseId) => {
    try {
      await axios.put(`${API}/admin/jcp-horses/${horseId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const toggleCourseActive = async (courseId) => {
    try {
      await axios.put(`${API}/admin/jcp-courses/${courseId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  const deleteHorse = async (horseId, horseName) => {
    if (!window.confirm(`Slet ${horseName}?`)) return;
    try {
      await axios.delete(`${API}/admin/jcp-horses/${horseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slettet');
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke slette');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Slet dette kursus?')) return;
    try {
      await axios.delete(`${API}/admin/jcp-courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slettet');
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke slette');
    }
  };

  // Edit customer
  const openEditCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setEditingCustomer({
        id: customer.id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email || ''
      });
      setShowEditCustomerForm(true);
    }
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/jcp-customers/${editingCustomer.id}`, {
        first_name: editingCustomer.first_name,
        last_name: editingCustomer.last_name,
        email: editingCustomer.email || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Kunde opdateret');
      setShowEditCustomerForm(false);
      setEditingCustomer(null);
      fetchAllData();
    } catch (error) {
      toast.error('Kunne ikke opdatere');
    }
  };

  // Get customer name by ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.first_name} ${customer.last_name}` : 'Ukendt';
  };

  // Generate months from start date to today only
  const getMonthsForHorse = (startDate) => {
    const months = [];
    const start = new Date(startDate + 'T00:00:00');
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (current <= now) {
      const monthStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
      const startDay = start.getDate();
      const daysInMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate();
      const adjustedStartDay = Math.min(startDay, daysInMonth);
      
      const periodStart = new Date(current.getFullYear(), current.getMonth(), adjustedStartDay);
      
      if (periodStart <= now) {
        const nextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
        const daysInNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate();
        const nextStartDay = Math.min(startDay, daysInNextMonth);
        const periodEnd = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), nextStartDay - 1);
        
        months.push({
          key: monthStr,
          periodStart,
          periodEnd,
          label: periodStart.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) + 
                 ' - ' + 
                 periodEnd.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
        });
      }
      
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }
    
    return months;
  };

  const formatPrice = (price) => price.toLocaleString('da-DK') + '€';

  const getServiceLabel = (type) => {
    const service = SERVICE_OPTIONS.find(s => s.type === type);
    return service ? service.label : type;
  };

  // Filter data
  const activeHorses = horses.filter(h => h.is_active !== false);
  const archivedHorses = horses.filter(h => h.is_active === false);
  const activeCourses = courses.filter(c => c.is_active !== false);
  const archivedCourses = courses.filter(c => c.is_active === false);
  const activeLessons = lessons.filter(l => l.is_active !== false);
  const archivedLessons = lessons.filter(l => l.is_active === false);
  const activeProvisions = provisions.filter(p => p.is_active !== false);
  const archivedProvisions = provisions.filter(p => p.is_active === false);

  // Calculate totals
  const now = new Date();
  
  // Helper to calculate unpaid amount for a horse
  const getUnpaidForHorse = (horse) => {
    const months = getMonthsForHorse(horse.start_date);
    const unpaidMonths = months.filter(m => {
      const isPaid = (horse.paid_months || []).includes(m.key);
      return !isPaid && m.periodEnd <= now;
    });
    return unpaidMonths.length * horse.price_per_month;
  };

  // Group data by customer and calculate per-customer totals
  const getCustomerData = () => {
    const customerMap = {};
    
    // Initialize all customers
    customers.forEach(c => {
      customerMap[c.id] = {
        customer: c,
        horses: [],
        courses: [],
        lessons: [],
        provisions: [],
        unpaidHorses: 0,
        unpaidCourses: 0,
        unpaidLessons: 0,
        unpaidProvisions: 0,
        totalUnpaid: 0
      };
    });
    
    // Add horses
    (showArchived ? archivedHorses : activeHorses).forEach(horse => {
      if (customerMap[horse.customer_id]) {
        customerMap[horse.customer_id].horses.push(horse);
        const unpaid = getUnpaidForHorse(horse);
        customerMap[horse.customer_id].unpaidHorses += unpaid;
      }
    });
    
    // Add courses
    (showArchived ? archivedCourses : activeCourses).forEach(course => {
      if (customerMap[course.customer_id]) {
        customerMap[course.customer_id].courses.push(course);
        if (!course.is_paid && new Date(course.course_date) <= now) {
          customerMap[course.customer_id].unpaidCourses += (course.total_price || course.num_days * 1200);
        }
      }
    });
    
    // Add lessons
    (showArchived ? archivedLessons : activeLessons).forEach(lesson => {
      if (customerMap[lesson.customer_id]) {
        customerMap[lesson.customer_id].lessons.push(lesson);
        if (!lesson.is_paid && new Date(lesson.lesson_date) <= now) {
          customerMap[lesson.customer_id].unpaidLessons += (lesson.total_price || lesson.num_sessions * lesson.price_per_session);
        }
      }
    });
    
    // Add provisions
    (showArchived ? archivedProvisions : activeProvisions).forEach(provision => {
      if (customerMap[provision.customer_id]) {
        customerMap[provision.customer_id].provisions.push(provision);
        if (!provision.is_paid && new Date(provision.provision_date) <= now) {
          customerMap[provision.customer_id].unpaidProvisions += provision.amount;
        }
      }
    });
    
    // Calculate totals and filter out empty customers
    return Object.values(customerMap)
      .map(data => {
        // Calculate displayUnpaid based on activeTab
        let displayUnpaid = 0;
        if (activeTab === 'all') {
          displayUnpaid = data.unpaidHorses + data.unpaidCourses + data.unpaidLessons + data.unpaidProvisions;
        } else if (activeTab === 'horses') {
          displayUnpaid = data.unpaidHorses;
        } else if (activeTab === 'courses') {
          displayUnpaid = data.unpaidCourses;
        } else if (activeTab === 'lessons') {
          displayUnpaid = data.unpaidLessons;
        } else if (activeTab === 'provisions') {
          displayUnpaid = data.unpaidProvisions;
        }
        
        return {
          ...data,
          totalUnpaid: data.unpaidHorses + data.unpaidCourses + data.unpaidLessons + data.unpaidProvisions,
          displayUnpaid: displayUnpaid  // This is what we show based on tab
        };
      })
      .filter(data => {
        // Filter based on active tab
        if (activeTab === 'all') {
          return data.horses.length > 0 || data.courses.length > 0 || data.lessons.length > 0 || data.provisions.length > 0;
        }
        if (activeTab === 'horses') return data.horses.length > 0;
        if (activeTab === 'courses') return data.courses.length > 0;
        if (activeTab === 'lessons') return data.lessons.length > 0;
        if (activeTab === 'provisions') return data.provisions.length > 0;
        return false;
      })
      .sort((a, b) => a.customer.last_name.localeCompare(b.customer.last_name));
  };

  const customerData = getCustomerData();
  
  // Toggle customer expansion
  const toggleCustomerExpanded = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const unpaidHorseTotal = activeHorses.reduce((sum, horse) => sum + getUnpaidForHorse(horse), 0);

  const unpaidCourseTotal = activeCourses
    .filter(c => !c.is_paid && new Date(c.course_date) <= now)
    .reduce((sum, c) => sum + (c.total_price || c.num_days * 1200), 0);

  const unpaidLessonTotal = activeLessons
    .filter(l => !l.is_paid && new Date(l.lesson_date) <= now)
    .reduce((sum, l) => sum + (l.total_price || l.num_sessions * l.price_per_session), 0);

  const unpaidProvisionTotal = activeProvisions
    .filter(p => !p.is_paid && new Date(p.provision_date) <= now)
    .reduce((sum, p) => sum + p.amount, 0);

  const totalUnpaid = unpaidHorseTotal + unpaidCourseTotal + unpaidLessonTotal + unpaidProvisionTotal;

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
          {/* Header */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            marginBottom: '1rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                padding: '0.25rem'
              }}
            >
              <ArrowLeft size={18} />
              Tilbage
            </button>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1 style={{ margin: '0 0 0.25rem 0', fontSize: '1.5rem', color: '#333' }}>
                  JCP Regningsoversigt
                </h1>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  Hold styr på månedlige regninger
                </p>
              </div>
              
              {/* Add buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowAddHorseForm(true)}
                  data-testid="add-horse-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.6rem 1rem',
                    background: '#7c6f5e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                >
                  <Plus size={16} />
                  Tilføj Træning
                </button>
                <button
                  onClick={() => setShowAddCourseForm(true)}
                  data-testid="add-course-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.6rem 1rem',
                    background: '#5a8b9e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                >
                  <Plus size={16} />
                  Tilføj Kursus
                </button>
                <button
                  onClick={() => setShowAddLessonForm(true)}
                  data-testid="add-lesson-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.6rem 1rem',
                    background: '#8b5a9e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                >
                  <Plus size={16} />
                  Tilføj Undervisning
                </button>
                <button
                  onClick={() => setShowAddProvisionForm(true)}
                  data-testid="add-provision-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.6rem 1rem',
                    background: '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}
                >
                  <Plus size={16} />
                  Tilføj Provision
                </button>
              </div>
            </div>

            {/* Unpaid total - shows based on active tab */}
            {(() => {
              let displayUnpaid = 0;
              let categoryLabel = 'regninger';
              
              if (activeTab === 'all') {
                displayUnpaid = totalUnpaid;
                categoryLabel = 'regninger i alt';
              } else if (activeTab === 'horses') {
                displayUnpaid = unpaidHorseTotal;
                categoryLabel = 'for træning';
              } else if (activeTab === 'courses') {
                displayUnpaid = unpaidCourseTotal;
                categoryLabel = 'for kurser';
              } else if (activeTab === 'lessons') {
                displayUnpaid = unpaidLessonTotal;
                categoryLabel = 'for undervisning';
              } else if (activeTab === 'provisions') {
                displayUnpaid = unpaidProvisionTotal;
                categoryLabel = 'for provision';
              }
              
              return displayUnpaid > 0 && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#fff3e0',
                  borderRadius: '8px',
                  color: '#e65100',
                  fontWeight: 500
                }}>
                  ⚠️ Ubetalte {categoryLabel}: {formatPrice(displayUnpaid)}
                </div>
              );
            })()}

            {/* Main tabs: All / Horses / Courses / Lessons / Provisions */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveTab('all')}
                data-testid="tab-all"
                style={{
                  padding: '0.5rem 1rem',
                  background: activeTab === 'all' ? '#333' : 'transparent',
                  color: activeTab === 'all' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                👥 Alle
              </button>
              <button
                onClick={() => setActiveTab('horses')}
                data-testid="tab-horses"
                style={{
                  padding: '0.5rem 1rem',
                  background: activeTab === 'horses' ? '#7c6f5e' : 'transparent',
                  color: activeTab === 'horses' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                🐴 Træning
              </button>
              <button
                onClick={() => setActiveTab('courses')}
                data-testid="tab-courses"
                style={{
                  padding: '0.5rem 1rem',
                  background: activeTab === 'courses' ? '#5a8b9e' : 'transparent',
                  color: activeTab === 'courses' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                📚 Kurser
              </button>
              <button
                onClick={() => setActiveTab('lessons')}
                data-testid="tab-lessons"
                style={{
                  padding: '0.5rem 1rem',
                  background: activeTab === 'lessons' ? '#8b5a9e' : 'transparent',
                  color: activeTab === 'lessons' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                📖 Undervisning
              </button>
              <button
                onClick={() => setActiveTab('provisions')}
                data-testid="tab-provisions"
                style={{
                  padding: '0.5rem 1rem',
                  background: activeTab === 'provisions' ? '#2e7d32' : 'transparent',
                  color: activeTab === 'provisions' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                💰 Provision
              </button>
            </div>

            {/* Archive filter */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                onClick={() => setShowArchived(false)}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: !showArchived ? '#f0f0f0' : 'transparent',
                  color: '#666',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Aktive
              </button>
              <button
                onClick={() => setShowArchived(true)}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: showArchived ? '#f0f0f0' : 'transparent',
                  color: '#666',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Arkiverede
              </button>
            </div>
          </div>

          {/* Add Horse Modal */}
          {showAddHorseForm && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>🐴 Tilføj Træning</h2>
                
                <form onSubmit={handleAddHorse}>
                  {/* Customer selection */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Kunde</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setHorseForm({ ...horseForm, is_new_customer: true, customer_id: '' })}
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: horseForm.is_new_customer ? '#7c6f5e' : '#f5f5f5',
                          color: horseForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Ny kunde
                      </button>
                      <button
                        type="button"
                        onClick={() => setHorseForm({ ...horseForm, is_new_customer: false })}
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: !horseForm.is_new_customer ? '#7c6f5e' : '#f5f5f5',
                          color: !horseForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Eksisterende
                      </button>
                    </div>

                    {horseForm.is_new_customer ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Fornavn"
                          value={horseForm.new_customer_first_name}
                          onChange={(e) => setHorseForm({ ...horseForm, new_customer_first_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                        <input
                          type="text"
                          placeholder="Efternavn"
                          value={horseForm.new_customer_last_name}
                          onChange={(e) => setHorseForm({ ...horseForm, new_customer_last_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      </div>
                    ) : (
                      <select
                        value={horseForm.customer_id}
                        onChange={(e) => setHorseForm({ ...horseForm, customer_id: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        <option value="">Vælg kunde...</option>
                        {customers.sort((a, b) => a.last_name.localeCompare(b.last_name)).map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Horse details */}
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Hestens Navn
                      </label>
                      <input
                        type="text"
                        value={horseForm.horse_name}
                        onChange={(e) => setHorseForm({ ...horseForm, horse_name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Startdato
                      </label>
                      <input
                        type="date"
                        value={horseForm.start_date}
                        onChange={(e) => setHorseForm({ ...horseForm, start_date: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Type
                      </label>
                      <select
                        value={horseForm.service_type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const service = SERVICE_OPTIONS.find(s => s.type === newType);
                          setHorseForm({ 
                            ...horseForm, 
                            service_type: newType,
                            price_per_month: service ? service.prices[0] : 600
                          });
                        }}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        {SERVICE_OPTIONS.map(option => (
                          <option key={option.type} value={option.type}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Pris pr. måned
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        {SERVICE_OPTIONS.find(s => s.type === horseForm.service_type)?.prices.map(price => (
                          <button
                            key={price}
                            type="button"
                            onClick={() => setHorseForm({ ...horseForm, price_per_month: price, use_custom_price: false })}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              padding: '0.6rem',
                              background: !horseForm.use_custom_price && horseForm.price_per_month === price ? '#7c6f5e' : '#f5f5f5',
                              color: !horseForm.use_custom_price && horseForm.price_per_month === price ? 'white' : '#666',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 500
                            }}
                          >
                            {formatPrice(price)}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setHorseForm({ ...horseForm, use_custom_price: true })}
                          style={{
                            flex: 1,
                            minWidth: '80px',
                            padding: '0.6rem',
                            background: horseForm.use_custom_price ? '#7c6f5e' : '#f5f5f5',
                            color: horseForm.use_custom_price ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          Specialpris
                        </button>
                      </div>
                      {horseForm.use_custom_price && (
                        <input
                          type="number"
                          placeholder="Indtast specialpris i kr"
                          value={horseForm.custom_price}
                          onChange={(e) => setHorseForm({ ...horseForm, custom_price: e.target.value })}
                          required
                          min="0"
                          data-testid="horse-custom-price"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowAddHorseForm(false); resetHorseForm(); }}
                      style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      style={{ flex: 1, padding: '0.75rem', background: '#7c6f5e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                    >
                      Tilføj
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Course Modal */}
          {showAddCourseForm && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>📚 Tilføj Kursus</h2>
                
                <form onSubmit={handleAddCourse}>
                  {/* Customer selection */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Kunde</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setCourseForm({ ...courseForm, is_new_customer: true, customer_id: '' })}
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: courseForm.is_new_customer ? '#5a8b9e' : '#f5f5f5',
                          color: courseForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Ny kunde
                      </button>
                      <button
                        type="button"
                        onClick={() => setCourseForm({ ...courseForm, is_new_customer: false })}
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: !courseForm.is_new_customer ? '#5a8b9e' : '#f5f5f5',
                          color: !courseForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Eksisterende
                      </button>
                    </div>

                    {courseForm.is_new_customer ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Fornavn"
                          value={courseForm.new_customer_first_name}
                          onChange={(e) => setCourseForm({ ...courseForm, new_customer_first_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                        <input
                          type="text"
                          placeholder="Efternavn"
                          value={courseForm.new_customer_last_name}
                          onChange={(e) => setCourseForm({ ...courseForm, new_customer_last_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      </div>
                    ) : (
                      <select
                        value={courseForm.customer_id}
                        onChange={(e) => setCourseForm({ ...courseForm, customer_id: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        <option value="">Vælg kunde...</option>
                        {customers.sort((a, b) => a.last_name.localeCompare(b.last_name)).map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Course details */}
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Kursusdato
                      </label>
                      <input
                        type="date"
                        value={courseForm.course_date}
                        onChange={(e) => setCourseForm({ ...courseForm, course_date: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Antal dage
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {[1, 2, 3].map(days => (
                          <button
                            key={days}
                            type="button"
                            onClick={() => setCourseForm({ ...courseForm, num_days: days })}
                            style={{
                              flex: 1,
                              padding: '0.75rem',
                              background: courseForm.num_days === days ? '#5a8b9e' : '#f5f5f5',
                              color: courseForm.num_days === days ? 'white' : '#666',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 500
                            }}
                          >
                            {days} dag{days > 1 ? 'e' : ''}
                          </button>
                        ))}
                      </div>
                      <div style={{ marginTop: '0.5rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
                        Total: {formatPrice(courseForm.num_days * 1200)}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Beskrivelse (valgfri)
                      </label>
                      <input
                        type="text"
                        value={courseForm.description}
                        onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                        placeholder="F.eks. lokation eller noter"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowAddCourseForm(false); resetCourseForm(); }}
                      style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      style={{ flex: 1, padding: '0.75rem', background: '#5a8b9e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                    >
                      Tilføj
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Customer Modal */}
          {showEditCustomerForm && editingCustomer && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '400px'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>✏️ Rediger Kunde</h2>
                
                <form onSubmit={handleEditCustomer}>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Fornavn
                      </label>
                      <input
                        type="text"
                        value={editingCustomer.first_name}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, first_name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Efternavn
                      </label>
                      <input
                        type="text"
                        value={editingCustomer.last_name}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, last_name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Email <span style={{ color: '#999', fontWeight: 400 }}>(til at sende noter)</span>
                      </label>
                      <input
                        type="email"
                        value={editingCustomer.email || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        placeholder="kunde@email.dk"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowEditCustomerForm(false); setEditingCustomer(null); }}
                      style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      style={{ flex: 1, padding: '0.75rem', background: '#7c6f5e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                    >
                      Gem
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Horse/Training Modal */}
          {editingHorse && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '400px'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>🐴 Rediger Træning</h2>
                <form onSubmit={handleEditHorse}>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Hestenavn</label>
                      <input
                        type="text"
                        value={editingHorse.horse_name}
                        onChange={(e) => setEditingHorse({ ...editingHorse, horse_name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Type</label>
                      <select
                        value={editingHorse.service_type}
                        onChange={(e) => setEditingHorse({ ...editingHorse, service_type: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        {SERVICE_OPTIONS.map(opt => (
                          <option key={opt.type} value={opt.type}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Pris pr. måned (EUR)</label>
                      <input
                        type="number"
                        value={editingHorse.price_per_month}
                        onChange={(e) => setEditingHorse({ ...editingHorse, price_per_month: parseInt(e.target.value) })}
                        required
                        min="0"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button type="button" onClick={() => setEditingHorse(null)} style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Annuller
                    </button>
                    <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#7c6f5e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
                      Gem
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Course Modal */}
          {editingCourse && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '400px'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>📚 Rediger Kursus</h2>
                <form onSubmit={handleEditCourse}>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Dato</label>
                      <input
                        type="date"
                        value={editingCourse.course_date}
                        onChange={(e) => setEditingCourse({ ...editingCourse, course_date: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Antal dage</label>
                      <input
                        type="number"
                        value={editingCourse.num_days}
                        onChange={(e) => setEditingCourse({ ...editingCourse, num_days: parseInt(e.target.value) })}
                        required
                        min="1"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Total pris (EUR)</label>
                      <input
                        type="number"
                        value={editingCourse.total_price || editingCourse.num_days * 1200}
                        onChange={(e) => setEditingCourse({ ...editingCourse, total_price: parseFloat(e.target.value) })}
                        required
                        min="0"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Beskrivelse</label>
                      <input
                        type="text"
                        value={editingCourse.description || ''}
                        onChange={(e) => setEditingCourse({ ...editingCourse, description: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button type="button" onClick={() => setEditingCourse(null)} style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Annuller
                    </button>
                    <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#5a8b9e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
                      Gem
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Lesson Modal */}
          {editingLesson && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '400px'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>📖 Rediger Undervisning</h2>
                <form onSubmit={handleEditLesson}>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Dato</label>
                      <input
                        type="date"
                        value={editingLesson.lesson_date}
                        onChange={(e) => setEditingLesson({ ...editingLesson, lesson_date: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Antal gange</label>
                      <input
                        type="number"
                        value={editingLesson.num_sessions}
                        onChange={(e) => setEditingLesson({ ...editingLesson, num_sessions: parseInt(e.target.value) })}
                        required
                        min="1"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Pris pr. gang (EUR)</label>
                      <input
                        type="number"
                        value={editingLesson.price_per_session}
                        onChange={(e) => setEditingLesson({ ...editingLesson, price_per_session: parseFloat(e.target.value) })}
                        required
                        min="0"
                        step="0.01"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Beskrivelse</label>
                      <input
                        type="text"
                        value={editingLesson.description || ''}
                        onChange={(e) => setEditingLesson({ ...editingLesson, description: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button type="button" onClick={() => setEditingLesson(null)} style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Annuller
                    </button>
                    <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#8b5a9e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
                      Gem
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Provision Modal */}
          {editingProvision && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '400px'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>💰 Rediger Provision</h2>
                <form onSubmit={handleEditProvision}>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Dato</label>
                      <input
                        type="date"
                        value={editingProvision.provision_date}
                        onChange={(e) => setEditingProvision({ ...editingProvision, provision_date: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Beløb (EUR)</label>
                      <input
                        type="number"
                        value={editingProvision.amount}
                        onChange={(e) => setEditingProvision({ ...editingProvision, amount: parseFloat(e.target.value) })}
                        required
                        min="0"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>Beskrivelse</label>
                      <input
                        type="text"
                        value={editingProvision.description || ''}
                        onChange={(e) => setEditingProvision({ ...editingProvision, description: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button type="button" onClick={() => setEditingProvision(null)} style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Annuller
                    </button>
                    <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
                      Gem
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Lesson Modal */}
          {showAddLessonForm && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>📖 Tilføj Undervisning</h2>
                
                <form onSubmit={handleAddLesson}>
                  {/* Customer selection */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Kunde</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setLessonForm({ ...lessonForm, is_new_customer: true, customer_id: '' })}
                        data-testid="lesson-new-customer-btn"
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: lessonForm.is_new_customer ? '#8b5a9e' : '#f5f5f5',
                          color: lessonForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Ny kunde
                      </button>
                      <button
                        type="button"
                        onClick={() => setLessonForm({ ...lessonForm, is_new_customer: false })}
                        data-testid="lesson-existing-customer-btn"
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: !lessonForm.is_new_customer ? '#8b5a9e' : '#f5f5f5',
                          color: !lessonForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Eksisterende
                      </button>
                    </div>

                    {lessonForm.is_new_customer ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Fornavn"
                          data-testid="lesson-first-name"
                          value={lessonForm.new_customer_first_name}
                          onChange={(e) => setLessonForm({ ...lessonForm, new_customer_first_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                        <input
                          type="text"
                          placeholder="Efternavn"
                          data-testid="lesson-last-name"
                          value={lessonForm.new_customer_last_name}
                          onChange={(e) => setLessonForm({ ...lessonForm, new_customer_last_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      </div>
                    ) : (
                      <select
                        value={lessonForm.customer_id}
                        onChange={(e) => setLessonForm({ ...lessonForm, customer_id: e.target.value })}
                        required
                        data-testid="lesson-customer-select"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        <option value="">Vælg kunde...</option>
                        {customers.sort((a, b) => a.last_name.localeCompare(b.last_name)).map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Lesson details */}
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Dato
                      </label>
                      <input
                        type="date"
                        value={lessonForm.lesson_date}
                        onChange={(e) => setLessonForm({ ...lessonForm, lesson_date: e.target.value })}
                        required
                        data-testid="lesson-date"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Antal gange
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map(num => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setLessonForm({ ...lessonForm, num_sessions: num })}
                            data-testid={`lesson-num-${num}`}
                            style={{
                              flex: 1,
                              minWidth: '50px',
                              padding: '0.75rem 0.5rem',
                              background: lessonForm.num_sessions === num ? '#8b5a9e' : '#f5f5f5',
                              color: lessonForm.num_sessions === num ? 'white' : '#666',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 500
                            }}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Pris pr. gang (EUR)
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <button
                          type="button"
                          onClick={() => setLessonForm({ ...lessonForm, price_per_session: 65, use_custom_price: false })}
                          data-testid="lesson-price-65"
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: !lessonForm.use_custom_price && lessonForm.price_per_session === 65 ? '#8b5a9e' : '#f5f5f5',
                            color: !lessonForm.use_custom_price && lessonForm.price_per_session === 65 ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          65€
                        </button>
                        <button
                          type="button"
                          onClick={() => setLessonForm({ ...lessonForm, price_per_session: 50, use_custom_price: false })}
                          data-testid="lesson-price-50"
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: !lessonForm.use_custom_price && lessonForm.price_per_session === 50 ? '#8b5a9e' : '#f5f5f5',
                            color: !lessonForm.use_custom_price && lessonForm.price_per_session === 50 ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          50€
                        </button>
                        <button
                          type="button"
                          onClick={() => setLessonForm({ ...lessonForm, use_custom_price: true })}
                          data-testid="lesson-price-custom"
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: lessonForm.use_custom_price ? '#8b5a9e' : '#f5f5f5',
                            color: lessonForm.use_custom_price ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          Andet
                        </button>
                      </div>
                      {lessonForm.use_custom_price && (
                        <input
                          type="number"
                          placeholder="Indtast beløb i EUR"
                          value={lessonForm.custom_price}
                          onChange={(e) => setLessonForm({ ...lessonForm, custom_price: e.target.value })}
                          required
                          min="0"
                          step="0.01"
                          data-testid="lesson-custom-price-input"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      )}
                      <div style={{ marginTop: '0.5rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
                        Total: {(lessonForm.num_sessions * (lessonForm.use_custom_price ? (parseFloat(lessonForm.custom_price) || 0) : lessonForm.price_per_session)).toFixed(2)}€
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Beskrivelse (valgfri)
                      </label>
                      <input
                        type="text"
                        value={lessonForm.description}
                        onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                        placeholder="F.eks. noter om undervisningen"
                        data-testid="lesson-description"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowAddLessonForm(false); resetLessonForm(); }}
                      data-testid="lesson-cancel-btn"
                      style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      data-testid="lesson-submit-btn"
                      style={{ flex: 1, padding: '0.75rem', background: '#8b5a9e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                    >
                      Tilføj
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Provision Modal */}
          {showAddProvisionForm && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '1rem'
            }}>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '1.5rem',
                width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>💰 Tilføj Provision</h2>
                
                <form onSubmit={handleAddProvision}>
                  {/* Customer selection */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Kunde</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setProvisionForm({ ...provisionForm, is_new_customer: true, customer_id: '' })}
                        data-testid="provision-new-customer-btn"
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: provisionForm.is_new_customer ? '#2e7d32' : '#f5f5f5',
                          color: provisionForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Ny kunde
                      </button>
                      <button
                        type="button"
                        onClick={() => setProvisionForm({ ...provisionForm, is_new_customer: false })}
                        data-testid="provision-existing-customer-btn"
                        style={{
                          flex: 1, padding: '0.5rem',
                          background: !provisionForm.is_new_customer ? '#2e7d32' : '#f5f5f5',
                          color: !provisionForm.is_new_customer ? 'white' : '#666',
                          border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        Eksisterende
                      </button>
                    </div>

                    {provisionForm.is_new_customer ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Fornavn"
                          data-testid="provision-first-name"
                          value={provisionForm.new_customer_first_name}
                          onChange={(e) => setProvisionForm({ ...provisionForm, new_customer_first_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                        <input
                          type="text"
                          placeholder="Efternavn"
                          data-testid="provision-last-name"
                          value={provisionForm.new_customer_last_name}
                          onChange={(e) => setProvisionForm({ ...provisionForm, new_customer_last_name: e.target.value })}
                          required
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                        />
                      </div>
                    ) : (
                      <select
                        value={provisionForm.customer_id}
                        onChange={(e) => setProvisionForm({ ...provisionForm, customer_id: e.target.value })}
                        required
                        data-testid="provision-customer-select"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      >
                        <option value="">Vælg kunde...</option>
                        {customers.sort((a, b) => a.last_name.localeCompare(b.last_name)).map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Provision details */}
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Dato
                      </label>
                      <input
                        type="date"
                        value={provisionForm.provision_date}
                        onChange={(e) => setProvisionForm({ ...provisionForm, provision_date: e.target.value })}
                        required
                        data-testid="provision-date"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Beløb (kr)
                      </label>
                      <input
                        type="number"
                        placeholder="Indtast beløb"
                        value={provisionForm.amount}
                        onChange={(e) => setProvisionForm({ ...provisionForm, amount: e.target.value })}
                        required
                        min="0"
                        data-testid="provision-amount"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', fontWeight: 500 }}>
                        Beskrivelse (valgfri)
                      </label>
                      <input
                        type="text"
                        value={provisionForm.description}
                        onChange={(e) => setProvisionForm({ ...provisionForm, description: e.target.value })}
                        placeholder="F.eks. hestens navn eller noter"
                        data-testid="provision-description"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowAddProvisionForm(false); resetProvisionForm(); }}
                      data-testid="provision-cancel-btn"
                      style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      data-testid="provision-submit-btn"
                      style={{ flex: 1, padding: '0.75rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                    >
                      Tilføj
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Content - Grouped by Customer */}
          {loading ? (
            <LoadingSkeleton type="card" count={3} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-testid="customer-list">
              {customerData.length === 0 ? (
                <div style={{ background: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center', color: '#666' }}>
                  {showArchived ? 'Ingen arkiverede poster' : 'Ingen data tilføjet endnu'}
                </div>
              ) : (
                customerData.map(data => {
                  const isExpanded = expandedCustomers[data.customer.id] !== false; // Default to expanded
                  
                  return (
                    <div 
                      key={data.customer.id} 
                      data-testid={`customer-card-${data.customer.id}`}
                      style={{ 
                        background: 'white', 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        border: data.displayUnpaid > 0 ? '2px solid #ff9800' : '1px solid #eee'
                      }}
                    >
                      {/* Customer Header */}
                      <div 
                        onClick={() => toggleCustomerExpanded(data.customer.id)}
                        style={{ 
                          padding: '1rem', 
                          background: data.displayUnpaid > 0 ? '#fff8e1' : '#f9f9f9',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: isExpanded ? '1px solid #eee' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>👤</span>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              {data.customer.first_name} {data.customer.last_name}
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditCustomer(data.customer.id); }}
                                title="Rediger kundenavn"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '0.15rem',
                                  cursor: 'pointer',
                                  color: '#999',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/admin/jcp/customer/${data.customer.id}/notes`); }}
                                title="Undervisningsnoter"
                                style={{
                                  background: '#e8f5e9',
                                  border: 'none',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  color: '#2e7d32',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              >
                                <Mic size={12} />
                                Noter
                              </button>
                            </h3>
                            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {data.horses.length > 0 && <span>🐴 {data.horses.length}</span>}
                              {data.courses.length > 0 && <span>📚 {data.courses.length}</span>}
                              {data.lessons.length > 0 && <span>📖 {data.lessons.length}</span>}
                              {data.provisions.length > 0 && <span>💰 {data.provisions.length}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {data.displayUnpaid > 0 && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.75rem', color: '#ff9800', fontWeight: 500 }}>
                                {activeTab === 'all' ? 'Ubetalt' : 
                                 activeTab === 'horses' ? 'Træning' :
                                 activeTab === 'courses' ? 'Kurser' :
                                 activeTab === 'lessons' ? 'Undervisning' :
                                 activeTab === 'provisions' ? 'Provision' : 'Ubetalt'}
                              </div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#e65100' }}>{formatPrice(data.displayUnpaid)}</div>
                            </div>
                          )}
                          <span style={{ color: '#999', fontSize: '1.2rem' }}>{isExpanded ? '▼' : '▶'}</span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{ padding: '1rem' }}>
                          {/* Horses Section */}
                          {(activeTab === 'all' || activeTab === 'horses') && data.horses.length > 0 && (
                            <div style={{ marginBottom: activeTab === 'all' ? '1rem' : 0 }}>
                              {activeTab === 'all' && (
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#7c6f5e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  🐴 Træning ({data.horses.length})
                                </h4>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {data.horses.map(horse => {
                                  const months = getMonthsForHorse(horse.start_date);
                                  const paidMonths = horse.paid_months || [];
                                  const unpaidAmount = getUnpaidForHorse(horse);
                                  
                                  return (
                                    <div key={horse.id} style={{ background: '#fafafa', borderRadius: '8px', padding: '0.75rem', borderLeft: `3px solid ${unpaidAmount > 0 ? '#ff9800' : '#4caf50'}` }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <div>
                                          <div style={{ fontWeight: 600, color: '#333' }}>{horse.horse_name}</div>
                                          <div style={{ fontSize: '0.8rem', color: '#666', display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                                            <span style={{ padding: '0.15rem 0.4rem', background: '#e3f2fd', color: '#1976d2', borderRadius: '3px', fontSize: '0.7rem' }}>
                                              {getServiceLabel(horse.service_type)}
                                            </span>
                                            <span>{formatPrice(horse.price_per_month)}/md</span>
                                          </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                          {unpaidAmount > 0 && (
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e65100', marginRight: '0.5rem' }}>
                                              {formatPrice(unpaidAmount)}
                                            </span>
                                          )}
                                          <button onClick={() => setEditingHorse({...horse})} title="Rediger" style={{ padding: '0.3rem', background: '#e3f2fd', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#1976d2' }}>
                                            <Pencil size={14} />
                                          </button>
                                          <button onClick={() => deleteHorse(horse.id)} style={{ padding: '0.3rem', background: '#ffebee', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                      </div>
                                      {/* Payment months */}
                                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                        {months.length === 0 ? (
                                          <span style={{ fontSize: '0.75rem', color: '#999' }}>Ingen betalinger endnu</span>
                                        ) : months.map(({ key, label, periodStart, periodEnd }) => {
                                          const isPaid = paidMonths.includes(key);
                                          // Show months where period has started
                                          if (periodStart > now) return null;
                                          
                                          // Determine status: paid, ongoing, or overdue
                                          const isOngoing = periodStart <= now && periodEnd >= now;
                                          const isOverdue = periodEnd < now && !isPaid;
                                          
                                          // Color scheme: Green = paid, White = ongoing, Red = overdue
                                          let bgColor, textColor, borderColor;
                                          if (isPaid) {
                                            bgColor = '#e8f5e9';
                                            textColor = '#2e7d32';
                                            borderColor = '#a5d6a7';
                                          } else if (isOngoing) {
                                            bgColor = '#ffffff';
                                            textColor = '#666';
                                            borderColor = '#ccc';
                                          } else if (isOverdue) {
                                            bgColor = '#ffebee';
                                            textColor = '#c62828';
                                            borderColor = '#ef9a9a';
                                          } else {
                                            bgColor = '#f5f5f5';
                                            textColor = '#666';
                                            borderColor = '#ddd';
                                          }
                                          
                                          return (
                                            <button
                                              key={key}
                                              onClick={() => toggleHorsePayment(horse.id, key, isPaid)}
                                              style={{
                                                padding: '0.25rem 0.5rem',
                                                fontSize: '0.7rem',
                                                background: bgColor,
                                                color: textColor,
                                                border: `1px solid ${borderColor}`,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.2rem',
                                                fontWeight: isOverdue ? 600 : 400
                                              }}
                                            >
                                              {isPaid && <Check size={10} />}
                                              {label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Courses Section */}
                          {(activeTab === 'all' || activeTab === 'courses') && data.courses.length > 0 && (
                            <div style={{ marginBottom: activeTab === 'all' ? '1rem' : 0 }}>
                              {activeTab === 'all' && (
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#5a8b9e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  📚 Kurser ({data.courses.length})
                                </h4>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {data.courses.map(course => {
                                  const totalPrice = course.total_price || course.num_days * 1200;
                                  return (
                                    <div key={course.id} style={{ background: '#fafafa', borderRadius: '8px', padding: '0.75rem', borderLeft: `3px solid ${course.is_paid ? '#4caf50' : '#ff9800'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                      <div>
                                        <div style={{ fontSize: '0.85rem', color: '#333' }}>
                                          {new Date(course.course_date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                                          {' • '}{course.num_days} dag{course.num_days > 1 ? 'e' : ''}
                                        </div>
                                        {course.description && <div style={{ fontSize: '0.75rem', color: '#999' }}>{course.description}</div>}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{formatPrice(totalPrice)}</span>
                                        <button
                                          onClick={() => toggleCoursePayment(course.id)}
                                          style={{ width: '28px', height: '28px', borderRadius: '50%', border: course.is_paid ? 'none' : '2px solid #ddd', background: course.is_paid ? '#4caf50' : 'white', color: course.is_paid ? 'white' : '#999', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          {course.is_paid ? <Check size={16} /> : null}
                                        </button>
                                        <button onClick={() => setEditingCourse({...course})} title="Rediger" style={{ padding: '0.3rem', background: '#e3f2fd', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#1976d2' }}>
                                          <Pencil size={14} />
                                        </button>
                                        <button onClick={() => deleteCourse(course.id)} style={{ padding: '0.3rem', background: '#ffebee', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Lessons Section */}
                          {(activeTab === 'all' || activeTab === 'lessons') && data.lessons.length > 0 && (
                            <div style={{ marginBottom: activeTab === 'all' ? '1rem' : 0 }}>
                              {activeTab === 'all' && (
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#8b5a9e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  📖 Undervisning ({data.lessons.length})
                                </h4>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {data.lessons.map(lesson => {
                                  const totalPrice = lesson.total_price || lesson.num_sessions * lesson.price_per_session;
                                  return (
                                    <div key={lesson.id} style={{ background: '#fafafa', borderRadius: '8px', padding: '0.75rem', borderLeft: `3px solid ${lesson.is_paid ? '#4caf50' : '#ff9800'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                      <div>
                                        <div style={{ fontSize: '0.85rem', color: '#333' }}>
                                          {new Date(lesson.lesson_date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                                          {' • '}{lesson.num_sessions} gang{lesson.num_sessions > 1 ? 'e' : ''} × {lesson.price_per_session}€
                                        </div>
                                        {lesson.description && <div style={{ fontSize: '0.75rem', color: '#999' }}>{lesson.description}</div>}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{totalPrice.toFixed(2)}€</span>
                                        <button
                                          onClick={() => toggleLessonPayment(lesson.id)}
                                          style={{ width: '28px', height: '28px', borderRadius: '50%', border: lesson.is_paid ? 'none' : '2px solid #ddd', background: lesson.is_paid ? '#4caf50' : 'white', color: lesson.is_paid ? 'white' : '#999', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          {lesson.is_paid ? <Check size={16} /> : null}
                                        </button>
                                        <button onClick={() => setEditingLesson({...lesson})} title="Rediger" style={{ padding: '0.3rem', background: '#e3f2fd', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#1976d2' }}>
                                          <Pencil size={14} />
                                        </button>
                                        <button onClick={() => deleteLesson(lesson.id)} style={{ padding: '0.3rem', background: '#ffebee', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Provisions Section */}
                          {(activeTab === 'all' || activeTab === 'provisions') && data.provisions.length > 0 && (
                            <div>
                              {activeTab === 'all' && (
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  💰 Provision ({data.provisions.length})
                                </h4>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {data.provisions.map(provision => (
                                  <div key={provision.id} style={{ background: '#fafafa', borderRadius: '8px', padding: '0.75rem', borderLeft: `3px solid ${provision.is_paid ? '#4caf50' : '#ff9800'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <div>
                                      <div style={{ fontSize: '0.85rem', color: '#333' }}>
                                        {new Date(provision.provision_date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </div>
                                      {provision.description && <div style={{ fontSize: '0.75rem', color: '#999' }}>{provision.description}</div>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{formatPrice(provision.amount)}</span>
                                      <button
                                        onClick={() => toggleProvisionPayment(provision.id)}
                                        style={{ width: '28px', height: '28px', borderRadius: '50%', border: provision.is_paid ? 'none' : '2px solid #ddd', background: provision.is_paid ? '#4caf50' : 'white', color: provision.is_paid ? 'white' : '#999', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        {provision.is_paid ? <Check size={16} /> : null}
                                      </button>
                                      <button onClick={() => setEditingProvision({...provision})} title="Rediger" style={{ padding: '0.3rem', background: '#e3f2fd', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#1976d2' }}>
                                        <Pencil size={14} />
                                      </button>
                                      <button onClick={() => deleteProvision(provision.id)} style={{ padding: '0.3rem', background: '#ffebee', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminJCPPage;
